import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import PageHero from '@/components/ui/PageHero';
import FAQSection from '@/components/ui/FAQSection';
import PricingSection from '@/components/ui/PricingSection';
import { Layout, Code, Database, Megaphone, Check, ExternalLink, MessageSquare, Upload, History, Users } from 'lucide-react';
import ScrollReveal from '@/components/ui/ScrollReveal';
import ServiceCard from '@/components/ui/ServiceCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const WebsiteDesign = () => {
  const faqs = [
    {
      question: "How long does it take to build a custom website?",
      answer: "Typically, our custom website projects take 4-8 weeks from start to finish, depending on the complexity and scope of the project. After our initial consultation, we'll provide you with a more specific timeline based on your requirements."
    },
    {
      question: "Do you provide website maintenance services?",
      answer: "Yes, we offer ongoing website maintenance packages to keep your site secure, updated, and performing optimally. Our maintenance services include regular updates, security checks, performance optimization, and content updates as needed."
    },
    {
      question: "Will my website be mobile-responsive?",
      answer: "Absolutely! All websites we design are fully responsive and optimized for all devices, including smartphones, tablets, and desktop computers. This ensures your visitors have a seamless experience regardless of the device they're using."
    },
    {
      question: "Can you help with website hosting?",
      answer: "Yes, we offer reliable hosting solutions that ensure your website is fast, secure, and always available. Our hosting packages include regular backups, security monitoring, and technical support."
    },
    {
      question: "Do you integrate SEO best practices into the website design?",
      answer: "Yes, we build all websites with SEO in mind from the ground up. This includes optimized code structure, fast loading times, proper heading hierarchy, mobile responsiveness, and other technical SEO elements that help your site rank better in search engines."
    }
  ];

  const pricingCards = [
    {
      title: "Landing Page Website",
      price: "Contact for Quote",
      description: "Perfect for small businesses looking to establish an online presence with a professional site.",
      features: [
        { text: "Important sections (About, Contact, Gallery)", included: true },
        { text: "Contact form", included: true },
        { text: "Social media links", included: true },
        { text: "Beautiful hero section", included: true },
        { text: "SEO optimization setup", included: true },
        { text: "Mobile responsive design", included: true },
        { text: "Contact information", included: true },
        { text: "Custom animations", included: false }
      ],
      popular: false,
      ctaText: "Request Quote"
    },
    {
      title: "10 Page Website",
      price: "Contact for Quote",
      description: "Comprehensive solution for growing businesses requiring more features and functionality.",
      features: [
        { text: "Up to 10 pages of content", included: true },
        { text: "Multiple contact forms", included: true },
        { text: "Advanced SEO setup", included: true },
        { text: "Content management system", included: true },
        { text: "Mobile responsive design", included: true },
        { text: "Social media integration", included: true },
        { text: "Custom animations", included: true },
        { text: "Analytics integration", included: true }
      ],
      popular: true,
      ctaText: "Request Quote"
    },
    {
      title: "Custom Development",
      price: "Contact for Quote",
      description: "Tailored website solutions for businesses with complex requirements and integrations.",
      features: [
        { text: "Custom platform development", included: true },
        { text: "Advanced functionality", included: true },
        { text: "Comprehensive SEO strategy", included: true },
        { text: "Content management system", included: true },
        { text: "Advanced form functionality", included: true },
        { text: "Custom features & integrations", included: true },
        { text: "Full social media integration", included: true },
        { text: "Custom requirements", included: true }
      ],
      popular: false,
      ctaText: "Get Custom Quote"
    }
  ];

  const portfolioWebsites = [
    {
      url: "https://pinnacleroofingbr.com",
      description: "Roofing Company in Baton Rouge",
      image: "https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?q=80&w=1974&auto=format&fit=crop&ixlib=rb-4.0.3"
    },
    {
      url: "https://thevladimircollection.co",
      description: "Luxury Real Estate Agent",
      image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3"
    },
    {
      url: "https://cagleyhomes.com",
      description: "Luxury and Custom Homebuilder in Baton Rouge",
      image: "https://images.unsplash.com/photo-1600585153490-76fb20a32601?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3"
    },
    {
      url: "https://hamayconstruction.com",
      description: "Remodeler, Contractor and Custom Homebuilder in Baton Rouge",
      image: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3"
    },
    {
      url: "https://wilsonenviro.com",
      description: "Environmental Construction Consultant in New Orleans",
      image: "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3"
    },
    {
      url: "https://envirohealthandwellness.com",
      description: "Environmental Construction Consultant in Baton Rouge",
      image: "https://images.unsplash.com/photo-1511818966892-d7d671e672a2?q=80&w=2071&auto=format&fit=crop&ixlib=rb-4.0.3"
    },
    {
      url: "https://buildwithlm.com",
      description: "Contractor in Denham Springs",
      image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=2089&auto=format&fit=crop&ixlib=rb-4.0.3"
    },
    {
      url: "https://tablasbr.com",
      description: "Custom Furniture Builder in Gonzales, Louisiana",
      image: "https://images.unsplash.com/photo-1581539250439-c96689b516dd?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3"
    },
    {
      url: "https://thecommonnomad.co",
      description: "Podcast Show",
      image: "https://images.unsplash.com/photo-1598327105740-820e04db502e?q=80&w=2071&auto=format&fit=crop&ixlib=rb-4.0.3"
    },
    {
      url: "https://therotondahouse.com",
      description: "Short Term Rental Unit",
      image: "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?q=80&w=2080&auto=format&fit=crop&ixlib=rb-4.0.3"
    }
  ];

  const addOns = [
    "Multiple contact forms", 
    "Advanced SEO ongoing monthly",
    "Social media management",
    "Paid ads",
    "Content management system",
    "Animations",
    "Custom features and integrations"
  ];

  const otherServices = [
    {
      title: "Website Hosting & Management",
      description: "We can host your current website and manage it for you if you'd like to switch from your current provider."
    },
    {
      title: "Framer.com Websites",
      description: "We can build your website with Framer.com technology, providing a modern and interactive experience."
    },
    {
      title: "Lovable.dev Development",
      description: "We can build websites with Lovable.dev, offering AI-powered development solutions."
    },
    {
      title: "Web Application Consulting",
      description: "We can consult on custom web applications or app development to meet your specific business needs."
    },
    {
      title: "Geo-Targeting Solutions",
      description: "If you need to deploy websites for local markets (geo-location targeting), we can help with effective solutions."
    },
    {
      title: "Sales Funnel Development",
      description: "We can help deploy multiple sales funnels to optimize your conversion rates across different market segments."
    }
  ];

  return (
    <PageLayout 
      title="Website Design & Development | Astrem" 
      description="Custom website design and development services for businesses and organizations in Baton Rouge and beyond."
      ogImage="public/lovable-uploads/b24584a6-a744-4fa6-bb55-b42c9b3d07f0.png"
    >
      <PageHero 
        title="Website Design & Development" 
        subtitle="Custom, beautiful, tailored websites that work" 
        description="We create beautiful, high-performing websites tailored to your business goals. Our focus is on creating an exceptional user experience that drives results and helps your business grow online."
        badge="Web Solutions"
        backgroundImage="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Person_working_on_laptop_coding.jpg"
        ctaText="Get Started"
        ctaLink="mailto:hello@astrem.co?subject=Web Design Inquiry"
      />
      
      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-4">
              Our Website Services
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-600 text-center max-w-2xl mx-auto mb-12">
              From simple brochure sites to complex custom solutions, we build websites that help you achieve your business goals.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ScrollReveal delay={200}>
              <ServiceCard
                icon={<Layout />}
                title="Custom Website Design"
                description="Unique websites tailored to your brand identity and business goals. We focus on creating a user experience that engages visitors and drives conversions."
                badge="Design"
              />
            </ScrollReveal>
            
            <ScrollReveal delay={300}>
              <ServiceCard
                icon={<Code />}
                title="Web Development"
                description="Custom development using modern technologies to create fast, secure, and scalable websites that provide an excellent user experience on all devices."
                badge="Development"
              />
            </ScrollReveal>
            
            <ScrollReveal delay={400}>
              <ServiceCard
                icon={<Database />}
                title="Content Management Systems"
                description="Easy-to-use content management systems that allow you to update your website content without technical knowledge. We specialize in WordPress, Framer, and custom CMS solutions."
                badge="CMS"
              />
            </ScrollReveal>
            
            <ScrollReveal delay={500}>
              <ServiceCard
                icon={<Megaphone />}
                title="Web Marketing Consulting"
                description="In addition to website development, we can consult on e-commerce solutions to help find the right platform for your business needs. We'll guide you through the options and implementation."
                badge="Consulting"
              />
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      {/* Process Section */}
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-4">
              Our Website Design Process
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-600 text-center max-w-2xl mx-auto mb-12">
              We follow a proven process to ensure your website meets your business goals and provides an exceptional user experience.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ScrollReveal delay={200}>
              <div className="bg-white p-8 rounded-xl border border-astrem-gray-200 text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-primary font-semibold text-xl">1</span>
                </div>
                <h3 className="text-xl font-medium mb-4">Discovery & Strategy</h3>
                <p className="text-astrem-gray-600">
                  We start by understanding your business, goals, target audience, and competitors to develop a strategic plan for your website.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={300}>
              <div className="bg-white p-8 rounded-xl border border-astrem-gray-200 text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-primary font-semibold text-xl">2</span>
                </div>
                <h3 className="text-xl font-medium mb-4">Design & Development</h3>
                <p className="text-astrem-gray-600">
                  Our team creates a visually appealing design and develops a responsive, user-friendly website using modern technologies.
                </p>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={400}>
              <div className="bg-white p-8 rounded-xl border border-astrem-gray-200 text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-primary font-semibold text-xl">3</span>
                </div>
                <h3 className="text-xl font-medium mb-4">Launch & Growth</h3>
                <p className="text-astrem-gray-600">
                  After thorough testing, we launch your website and provide ongoing support to ensure it continues to meet your business needs.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      {/* Portfolio Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-4">
              Our Website Portfolio
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-600 text-center max-w-2xl mx-auto mb-12">
              Explore some of the custom websites we've designed and developed for our clients.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolioWebsites.map((website, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <a href={website.url} target="_blank" rel="noopener noreferrer" className="block h-full">
                  <div className="relative h-full group overflow-hidden rounded-xl bg-white shadow-md border border-astrem-gray-200 hover:shadow-lg transition-all duration-300">
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent z-10"></div>
                    <img 
                      src={website.image} 
                      alt={website.description} 
                      className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute bottom-0 left-0 right-0 p-4 z-20">
                      <div className="backdrop-blur-sm bg-white/10 p-4 rounded-lg">
                        <h3 className="text-white font-medium mb-1 flex items-center">
                          {website.url.replace('https://', '')}
                          <ExternalLink className="ml-2 h-4 w-4" />
                        </h3>
                        <p className="text-white/90 text-sm">{website.description}</p>
                      </div>
                    </div>
                  </div>
                </a>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      {/* Website Add-Ons Section */}
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-4">
              Website Add-Ons
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-600 text-center max-w-2xl mx-auto mb-12">
              Enhance your website with these additional features and services.
            </p>
          </ScrollReveal>
          
          <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
            {addOns.map((addon, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <div className="flex items-center bg-white p-4 rounded-xl border border-astrem-gray-200">
                  <div className="mr-3 bg-green-100 text-green-600 rounded-full p-1">
                    <Check className="h-4 w-4" />
                  </div>
                  <span className="text-astrem-gray-700 font-medium">
                    {addon}
                  </span>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      {/* Other Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium text-center mb-4">
              Other Website Services
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-astrem-gray-600 text-center max-w-2xl mx-auto mb-12">
              Beyond standard web design, we offer a variety of specialized services to meet your digital needs.
            </p>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {otherServices.map((service, index) => (
              <ScrollReveal key={index} delay={index * 100}>
                <Card className="h-full border border-astrem-gray-200">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-medium mb-3">{service.title}</h3>
                    <p className="text-astrem-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
          
          <ScrollReveal delay={400}>
            <div className="text-center mt-10">
              <Button asChild className="bg-primary hover:bg-primary/90 text-white rounded-full">
                <a href="mailto:hello@astrem.co?subject=Web Design Inquiry">
                  Discuss Your Website Needs
                </a>
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>
      
      {/* Franchise Website Section */}
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  Multi-Location Businesses
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Franchise & Multi-Location Website Solutions
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-6">
                  Perfect for franchises or service-based businesses that need multiple websites deployed for different cities or locations.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Centralized content management system
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      City-specific SEO optimization
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Make changes to individual locations or all at once
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 bg-green-100 text-green-600 rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                    <span className="text-astrem-gray-700">
                      Consistent branding with local customization
                    </span>
                  </li>
                </ul>
                <Button asChild className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full">
                  <a href="mailto:hello@astrem.co?subject=Franchise Web Solutions Inquiry">Learn More About Franchise Solutions</a>
                </Button>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <img 
                src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Person_working_on_laptop_coding.jpg" 
                alt="Multi-location website management" 
                className="rounded-xl shadow-lg w-full" 
              />
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      {/* Revision Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  Seamless Collaboration
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  Website Revisions Made Simple
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-8">
                  We've made website revisions simple, quick, and convenient. Through our special review links, you can directly comment and upload photos to a live website canvas for us to integrate, ensuring a smooth process.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <div className="mr-3 mt-1.5 text-primary">
                      <MessageSquare className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-medium mb-1">Real-Time Feedback</h3>
                      <p className="text-astrem-gray-600">Leave comments directly on specific elements of your website during development</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="mr-3 mt-1.5 text-primary">
                      <Upload className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-medium mb-1">Easy Asset Uploads</h3>
                      <p className="text-astrem-gray-600">Upload new photos and content directly through our review interface</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="mr-3 mt-1.5 text-primary">
                      <History className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-medium mb-1">Quick Turnaround</h3>
                      <p className="text-astrem-gray-600">See your requested changes implemented rapidly with our streamlined process</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="mr-3 mt-1.5 text-primary">
                      <Users className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-medium mb-1">Team Collaboration</h3>
                      <p className="text-astrem-gray-600">Invite team members to review and provide feedback in one central location</p>
                    </div>
                  </div>
                </div>
                <Button asChild className="rounded-full">
                  <a href="mailto:hello@astrem.co?subject=Website Design Collaboration">
                    Start Your Collaborative Project
                  </a>
                </Button>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=1000&auto=format&fit=crop"
                  alt="Website Revision Process"
                  className="rounded-xl shadow-lg w-full"
                />
                <div className="absolute -bottom-4 -right-4 bg-white p-4 rounded-xl shadow-lg">
                  <div className="flex items-center space-x-2">
                    <Check className="h-5 w-5 text-green-500" />
                    <span className="text-sm font-medium">Quick & Easy Revisions</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-astrem-black text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
              Ready to Transform Your Online Presence?
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
              Let's discuss how we can create a website that helps you achieve your business goals and connect with your target audience.
            </p>
          </ScrollReveal>
          
          <ScrollReveal delay={200}>
            <Button asChild size="lg" className="bg-white text-astrem-black hover:bg-astrem-gray-100 rounded-full">
              <a href="mailto:hello@astrem.co?subject=Web Design Inquiry">Start Your Website Project</a>
            </Button>
          </ScrollReveal>
        </div>
      </section>
      
      <PricingSection 
        title="Website Solutions" 
        description="Contact us for a custom quote tailored to your specific needs and requirements."
        pricingCards={pricingCards}
      />
      
      <FAQSection faqs={faqs} />
    </PageLayout>
  );
};

export default WebsiteDesign;
